/**
 * Bomu Labs — Welcome popup behavior.
 *
 * Handles:
 * - Delayed and/or scroll-triggered display (whichever happens first)
 * - Suppression via localStorage for a configurable number of days
 * - Accessible open/close via the native <dialog> element (which gives us
 *   focus trapping and Escape-to-close for free)
 * - Progressive-enhancement submission of Shopify's native customer form
 *   (falls back to a normal full-page form submit if fetch fails)
 */
class BomuWelcomePopup extends HTMLElement {
  static SUPPRESS_KEY = 'bomu_welcome_popup_dismissed_until';
  static SUBSCRIBED_KEY = 'bomu_welcome_popup_subscribed';

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.delaySeconds = parseInt(this.dataset.delaySeconds, 10) || 10;
    this.scrollPercent = parseInt(this.dataset.scrollPercent, 10) || 35;
    this.suppressDays = parseInt(this.dataset.suppressDays, 10) || 14;
    this.isDesignMode = this.dataset.designMode === 'true';

    this._bindCloseControls();
    this._bindForm();

    // Never auto-trigger inside the theme editor; merchants can still open
    // it manually (e.g. via browser devtools or a preview link) if needed.
    if (this.isDesignMode) return;

    if (this._isSuppressed()) return;

    this._armTriggers();
  }

  _isSuppressed() {
    try {
      if (localStorage.getItem(BomuWelcomePopup.SUBSCRIBED_KEY) === '1') return true;
      const until = parseInt(localStorage.getItem(BomuWelcomePopup.SUPPRESS_KEY) || '0', 10);
      return Date.now() < until;
    } catch (e) {
      // localStorage unavailable (private browsing, blocked, etc.) — fail
      // open rather than throwing, but this means suppression won't work.
      return false;
    }
  }

  _armTriggers() {
    let shown = false;

    const show = () => {
      if (shown) return;
      shown = true;
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      this._show();
    };

    const timer = setTimeout(show, this.delaySeconds * 1000);

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = (window.scrollY / scrollable) * 100;
      if (percent >= this.scrollPercent) show();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  _show() {
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    // Mark as shown/suppressed immediately so navigating to another page
    // (without explicitly dismissing) doesn't cause it to reappear.
    this._persistSuppression();
  }

  _persistSuppression() {
    try {
      const until = Date.now() + this.suppressDays * 24 * 60 * 60 * 1000;
      localStorage.setItem(BomuWelcomePopup.SUPPRESS_KEY, String(until));
    } catch (e) {
      /* localStorage unavailable — popup may show more than once */
    }
  }

  _bindCloseControls() {
    this.querySelectorAll('[data-bomu-popup-close]').forEach((el) => {
      el.addEventListener('click', () => this._close());
    });
    // Covers Escape-to-close and clicking the ::backdrop, both of which
    // fire the dialog's native "close"/"cancel" events.
    this.dialog.addEventListener('cancel', () => this._persistSuppression());
    this.dialog.addEventListener('close', () => this._persistSuppression());
  }

  _close() {
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  }

  _bindForm() {
    const form = this.querySelector('#bomu-popup-form');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submitButton = form.querySelector('.bomu-popup__submit');
      if (submitButton) submitButton.disabled = true;

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'text/html' },
        });

        if (response.ok) {
          this._onSubscribed();
        } else {
          form.submit();
        }
      } catch (err) {
        form.submit();
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    });
  }

  _onSubscribed() {
    try {
      localStorage.setItem(BomuWelcomePopup.SUBSCRIBED_KEY, '1');
    } catch (e) {
      /* ignore */
    }
    const formState = this.querySelector('[data-bomu-popup-form-state]');
    const success = this.querySelector('[data-bomu-popup-success]');
    if (formState) formState.hidden = true;
    if (success) success.hidden = false;
  }
}

customElements.define('bomu-welcome-popup', BomuWelcomePopup);
